import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * Modal wrapping the VC+ Ordering UI for an in-flight lab request.
 *
 * IDEXX's docs (`partner-requisition-data.txt §Detecting Order Completion`)
 * describe two completion signals when the user finishes the order in the
 * VC+ UI:
 *   1. The frame navigates to `/ui/done`
 *   2. VC+ posts a cross-window message whose `data` starts with `'ui/done'`
 *
 * Same-origin iframe navigation is blocked by IDEXX's frame-ancestors policy
 * in most browsers, so we rely on postMessage as the primary signal. The
 * "Open in new tab" fallback handles environments where the iframe is blocked.
 */

interface IdexxOrderingIframeProps {
  open: boolean;
  uiUrl: string | null;
  /** Called once IDEXX signals completion; the parent should fetch the latest order state. */
  onDone: () => void;
  /** Called when the user closes the modal manually. */
  onClose: () => void;
}

/**
 * Accept any https origin under vetconnectplus.com rather than pinning
 * specific hosts: IDEXX's hostnames differ between their test environment
 * (integration./partner.) and production (ordering./www.), and only IDEXX
 * controls subdomains of vetconnectplus.com, so the suffix check is as safe
 * as an exact-match list without breaking when the environment (or IDEXX's
 * infra) changes.
 */
function isIdexxOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === 'https:' &&
      (url.hostname === 'vetconnectplus.com' ||
        url.hostname.endsWith('.vetconnectplus.com'))
    );
  } catch {
    return false;
  }
}

const IdexxOrderingIframe: React.FC<IdexxOrderingIframeProps> = ({
  open,
  uiUrl,
  onDone,
  onClose,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [doneSignal, setDoneSignal] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Reset state when the modal is reopened with a new URL.
  useEffect(() => {
    if (open) {
      setIframeLoaded(false);
      setIframeError(false);
      setDoneSignal(false);
    }
  }, [open, uiUrl]);

  // Listen for the VC+ "ui/done" postMessage. We check origin first so a
  // hostile page can't fake completion. The payload format per IDEXX docs is
  // a string starting with "ui/done"; we accept that plus object variants
  // ({ type: 'ui/done' }) for forward compat.
  useEffect(() => {
    if (!open) return;
    function handler(event: MessageEvent) {
      if (!isIdexxOrigin(event.origin)) return;
      const payload = event.data;
      const isDone =
        (typeof payload === 'string' && payload.indexOf('ui/done') === 0) ||
        (payload && typeof payload === 'object' && payload.type === 'ui/done');
      if (isDone) {
        setDoneSignal(true);
        // Give VC+ a beat to settle then notify the parent.
        setTimeout(() => onDone(), 250);
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open, onDone]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Box>
          Finalize order in VC+
          <Typography variant="caption" component="div" color="text.secondary">
            Pick or adjust tests, enter specimen details, then click "Order" inside VC+ to submit.
          </Typography>
        </Box>
        <Box>
          {uiUrl && (
            <Tooltip title="Open in a new tab">
              <IconButton
                size="small"
                onClick={() => window.open(uiUrl, '_blank', 'noopener,noreferrer')}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '70vh', position: 'relative' }}>
        {!uiUrl ? (
          <Alert severity="error" sx={{ m: 2 }}>
            No VC+ URL is available for this order. Try refreshing the order from IDEXX.
          </Alert>
        ) : (
          <>
            {!iframeLoaded && !iframeError && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="background.paper"
                zIndex={1}
              >
                <Box textAlign="center">
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading VC+ Ordering…
                  </Typography>
                </Box>
              </Box>
            )}
            {iframeError && (
              <Alert severity="warning" sx={{ m: 2 }}>
                The VC+ Ordering UI can't be embedded here (likely a browser frame policy).
                Use "Open in new tab" above to finalize the order; when you're done in VC+,
                close this dialog and click <strong>Refresh</strong> on the order row.
              </Alert>
            )}
            {doneSignal && (
              <Alert severity="success" sx={{ m: 2 }}>
                VC+ reported completion. Pulling the updated order state from IDEXX…
              </Alert>
            )}
            <iframe
              ref={iframeRef}
              src={uiUrl}
              title="VetConnect+ Ordering"
              style={{ width: '100%', height: '100%', border: 'none' }}
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeError(true)}
              // Allow form submission and same-origin so the VC+ UI can do
              // its normal thing inside the frame.
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>I'll finish later</Button>
        <Button variant="contained" onClick={onDone}>
          I'm done — refresh order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdexxOrderingIframe;
